// 订单相关类型定义（自动生成，请勿手动修改）

import type { Address } from "../address/schema"
import type { Spu } from "../product/product.types"
import type { Store } from "../store-list/types"

// ===================== Refund =====================
export enum RefundStatusEnum {
	WAIT = "wait",
	PROCESSING = "processing",
	SUCCESS = "success",
	REJECT = "reject",
}

export enum RefundTypeEnum {
	ORDER = "order",
	FREIGHT = "freight",
}

export interface OrderRefund {
	id: string
	refundNum: string
	refundType: RefundTypeEnum
	userId: string
	orderNumber: string
	orderDispatchNumbers: string[]
	payments: string[]
	refundReasonID: string
	refundReason: string
	refundAmount: number
	refundFreightAmount: number
	taxAmount: number
	refundStatus: RefundStatusEnum
	remark: string
	images: string[]
	refundItems: string[]
	error: string
	createdAt: string
	updatedAt: string
	deletedAt?: string | null
}

// ===================== Order =====================
// 订单状态枚举
export enum OrderStatusEnum {
	WAIT_PAY = "waitPay", //待支付
	PAID = "paid", //已支付 也是门店购的待接单
	PUSHED = "pushed", //已推送
	PART_SENDED = "partSended", //部分发货
	SENDED = "sended", //全部发货  也是门店购的待提货
	CLOSE = "close", //关闭

	// 门店购订单状态
	// WAIT_SEND = "waitSend", //废弃
	SUCCESS = "success", //已完成
	REJECTED = "rejected", //已拒单
	TIMEOUT = "timeout", //已超时
}

// 订单类型枚举
export enum OrderTypeEnum {
	// SECRET = "secret", // 抽盒
	// PRESELL = "presell", // 预售
	SHOP = "shop", // 普通订单
	// GIFT = "gift", // 赠品
	// FREIGHT = "freight", // 免运费
	// VIRTUAL = "virtual", // 虚拟商品
	// SCORE = "score", // 积分商品
	// DRAW = "draw", // 抽选订单
	// DEPOSIT = "deposit", // 定金订单
	// FREE = "free", // 0元购
	STORE = "store", // 门店购订单
}

//支付状态枚举
export enum PayStatusEnum {
	WAIT_PAY = "waitPay", //待支付
	PAID = "paid", //已支付
	REFUNDING = "refunding", //退款中
	PART_REFUND = "partRefund", //部分退款
	REFUND = "refund", //退款
}

export enum OrderPlatformEnum {
	WEB = "web",
	APP = "app",
}

export enum OrderChannelEnum {
	STORE = "store",
	SHOP = "shop",
	SECRET = "secret",
	LIVE_SECRET = "liveSecret",
	LIVE_SHOP = "liveShop",
}

export enum CloseType {
	AUTO = "auto",
	MANUAL = "manual",
	REFUND = "refund",
	FORCE = "force",
}

export interface OrderTax {
	type: string
	point: number
	amount: number
	refundAmount: number
}

export enum OrderItemStatus {
	waitSend = "waitSend",
	sending = "sending",
	sended = "sended",
}

export enum InvoiceTypeEnum {
	PERSONAL = "personal",
	COMPANY = "company",
}

export enum DispatchStatusEnum {
	WAIT_PAY = "waitPay",
	PAID = "paid",
	SENT = "sent",
	PART_EXPRESSED = "partExpressed",
	EXPRESSED = "expressed",
	REFUND = "refund",
	CLOSE = "close",
}

export enum DispatchPushStatus {
	wait = "wait",
	ok = "ok",
	error = "error",
}

// 订单主表
export interface Order {
	id: string
	status: OrderStatusEnum
	orderType: OrderTypeEnum
	payStatus: PayStatusEnum
	platform: OrderPlatformEnum
	channel: OrderChannelEnum
	channelId: string
	source: string
	userId: string
	userEmail: string
	userPhone: string
	userType: string
	orderNumber: string
	totalAmount: number
	payAmount: number
	discountAmount: number
	userCouponId: string
	couponId: string
	refundAmount: number
	refundFreightAmount: number
	tax: OrderTax | null
	payMethod: string
	payTime?: string | null
	invalid: boolean
	invalidReason: string
	closeReason: string
	autoCloseTime?: string | null
	areaCode: string
	currency: string
	payNumber: string
	remark: string
	dispute: boolean
	disputeReason: string
	refundChannel: string
	warehouse: string
	invoice: boolean
	invoiced: boolean
	addressesID: string
	invoiceAddressId: string
	createdAt: string
	updatedAt: string
	deletedAt?: string | null
	verifyCode?: string | null
	verifyTime?: string | null
	queueCode?: string | null
}

// 订单商品
export interface OrderItem {
	id: string
	orderNumber: string
	orderItemNum: string
	status: OrderItemStatus
	spuId: string
	spu: Spu
	skuId: string
	activityType: string
	warehouse: string
	points: number
	price: number
	payPrice: number
	tax: number
	currency: string
	sended: boolean
	couponDiscount: number
	refund: boolean
}

// 订单商品快照
export interface OrderItemSnapshot {
	orderItemNum: string
	orderNumber: string
	spu: Spu
	sku: {
		name_trans: Record<string, string>
		skuCode: string
		mainImage: string
	}
}

// 发货单
export interface OrderDispatch {
	id: string
	address?: Address
	orderDispatchNum: string
	userId: string
	freightAmount: number
	payTime?: string | null
	areaCode: string
	currency: string
	payNumber: string
	warehouse: string
	dispatchTime?: string | null
	status: DispatchStatusEnum
	expressNumber: string
	expressCompanyId: string
	expressCompany: string
	pushStatus: DispatchPushStatus
	refund: boolean
	pushCount: number
	pushError: string
	createdAt: string
	updatedAt: string
	deletedAt?: string | null
}

// 发货单快照
export interface OrderDispatchSnapshot {
	orderDispatchNum: string
	address: Address
}

// 发货单商品
export interface OrderDispatchItem {
	orderDispatchNum: string
	orderNum: string
	orderItemNum: string
}

//图片类型
export enum ImageTextType {
	IMAGE = "image",
	VIDEO = "video",
}

export interface OrderDetailItem {
	orderItemNum: string
	status: OrderItemStatus
	spuId: string
	skuId: string
	activityType: string
	warehouse: string
	points: number
	price: number
	payPrice: number
	tax: number
	currency: string
	couponDiscount: number
	refund: boolean
	expressCode: string
	expressID: string
	expressName_trans: Record<string, string>
	dispatchTime?: string | null
	spu: {
		nonRefundable: boolean
		name_trans: Record<string, string>
		subTitle_trans: Record<string, string>
		type: string
		expectedDeliveryTime: Date | string | null
		bannerImages: Array<{
			type?: ImageTextType
			link_trans?: Record<string, string>
			coverImage_trans?: Record<string, string>
		}>
	}
	sku: {
		name_trans: Record<string, string>
		skuCode: string
		mainImage: string
	}
}

// 订单发货单关联
export interface OrderDispatchRelation {
	orderDispatchNum: string
	orderNum: string
}
export interface UserOrderDetail {
	id: string
	status: OrderStatusEnum
	orderType: OrderTypeEnum
	payStatus: PayStatusEnum
	platform: OrderPlatformEnum
	channel: OrderChannelEnum
	channelId: string
	source: string
	userId: string
	userEmail: string
	userPhone: string
	userType: string
	orderNumber: string
	totalAmount: number
	payAmount: number
	discountAmount: number
	userCouponId: string
	couponId: string
	refundAmount: number
	refundFreightAmount: number
	tax: {
		type: string
		point: number
		amount: number
		refundAmount: number
	} | null
	payMethod: string
	payTime: string | null
	invalid: boolean
	invalidReason: string
	closeReason: string
	autoCloseTime: string | null
	areaCode: string
	currency: string
	payNumber: string
	remark: string
	dispute: boolean
	disputeReason: string
	refundChannel: string
	warehouse: string
	invoice: boolean
	invoiced: boolean
	addressesID: string
	invoiceAddressId: string
	createdAt: string
	updatedAt: string
	items: OrderDetailItem[]
	dispatches: OrderDispatch[]
	refundStatus?: RefundStatusEnum
	store?: Store
	verifyCode?: string
	queueCode?: string
	payOrderMethod?: string
}

export type UserOrder = Order & {
	store?: { name: string; address: string }
	items: OrderDetailItem[]
	freightAmount?: number
}
